import axios from 'axios';

/**
 * WB-PP-01/02: browser → same-origin BFF only (/api/proxy).
 * JWT lives in httpOnly cookie set by BFF — never in sessionStorage.
 */
const API = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api/proxy',
  withCredentials: true,
});

API.interceptors.request.use((config) => {
  if (config.headers) {
    delete config.headers.Authorization;
  }
  return config;
});

const applyCsrfHeader = (token) => {
  if (token) {
    API.defaults.headers.common['x-csrf-token'] = token;
  } else {
    delete API.defaults.headers.common['x-csrf-token'];
  }
};

const isCsrfTokenRequest = (config) => {
  const url = String(config?.url || '');
  return url.includes('/csrf-token');
};

const isInvalidCsrfError = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status !== 403) {
    return false;
  }
  if (data?.error === 'INVALID_CSRF_TOKEN') {
    return true;
  }
  return /csrf token/i.test(String(data?.message || ''));
};

let csrfRefreshPromise = null;

const refreshCsrfToken = () => {
  if (!csrfRefreshPromise) {
    csrfRefreshPromise = API.get('/project-panel/csrf-token')
      .then((response) => {
        const token = response?.data?.csrfToken;
        applyCsrfHeader(token);
        return token;
      })
      .finally(() => {
        csrfRefreshPromise = null;
      });
  }
  return csrfRefreshPromise;
};

const setRequestCsrfHeader = (config, token) => {
  if (!config.headers) {
    config.headers = {};
  }
  if (typeof config.headers.set === 'function') {
    config.headers.set('x-csrf-token', token);
  } else {
    config.headers['x-csrf-token'] = token;
  }
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    if (
      !original ||
      original._csrfRetry ||
      isCsrfTokenRequest(original) ||
      !isInvalidCsrfError(error)
    ) {
      return Promise.reject(error);
    }

    original._csrfRetry = true;

    try {
      const token = await refreshCsrfToken();
      if (!token) {
        return Promise.reject(error);
      }
      setRequestCsrfHeader(original, token);
      return API(original);
    } catch (_) {
      return Promise.reject(error);
    }
  }
);

export default API;
